// Opportunity Circle — API client (cookie auth, maps BE rows → UI model)
// Same-origin in production when unset (nginx proxies /api). Dev uses Vite proxy or localhost.
const API_BASE = (import.meta.env.VITE_API_BASE != null && String(import.meta.env.VITE_API_BASE).trim() !== '')
  ? String(import.meta.env.VITE_API_BASE).trim()
  : (import.meta.env.DEV ? 'http://localhost:5000' : '');

  async function apiFetch(path, options = {}) {
    const headers = { ...(options.headers || {}) };
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    let res;
    try {
      res = await fetch(`${API_BASE}${path}`, {
        ...options,
        credentials: 'include',
        headers,
      });
    } catch (err) {
      throw new Error(
        `Cannot reach API at ${API_BASE}. Start the backend: cd opportunity_circle_be && npm run dev`
      );
    }
    let data = {};
    try {
      data = await res.json();
    } catch (e) {
      data = {};
    }
    if (!res.ok) {
      const err = new Error(data.message || `Request failed (${res.status})`);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  function daysLeftFromDeadline(deadline) {
    if (!deadline) return null;
    const end = new Date(deadline);
    if (Number.isNaN(end.getTime())) return null;
    const diff = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff < 0 ? -1 : diff;
  }

  function formatPosted(createdAt) {
    if (!createdAt) return 'Recently';
    const ms = Date.now() - new Date(createdAt).getTime();
    const hours = Math.floor(ms / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(createdAt).toLocaleDateString();
  }

  function formatLocation(row) {
    if (row.locationRaw) return row.locationRaw;
    const parts = [];
    if (row.city) parts.push(row.city);
    if (row.countryCode) parts.push(row.countryCode);
    if (row.isRemote) parts.push('Remote');
    return parts.length ? parts.join(' · ') : 'Location TBD';
  }

  function normalizeSkills(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return String(value).split(',').map((skill) => skill.trim()).filter(Boolean);
  }

  function formatSalaryRange(details, extraDetails) {
    const source = { ...(extraDetails || {}), ...(details || {}) };
    const currency = source.currency || '';
    const min = source.salaryMin;
    const max = source.salaryMax;
    if (min && max) return `${currency} ${min}–${max}`.trim();
    if (min) return `${currency} ${min}+`.trim();
    if (max) return `Up to ${currency} ${max}`.trim();
    return null;
  }

  function formatStipend(details, extraDetails) {
    const source = { ...(extraDetails || {}), ...(details || {}) };
    const amount = source.stipendAmount;
    if (!amount) return null;
    const currency = source.currency || '';
    return `${currency} ${amount}/mo`.trim();
  }

  function buildSummaryFields(categorySlug, details, extraDetails) {
    const merged = { ...(extraDetails || {}), ...(details || {}) };
    switch (categorySlug) {
      case 'jobs':
        return {
          salary: formatSalaryRange(details, extraDetails) || '—',
          skills: normalizeSkills(merged.requiredSkills),
          type: merged.employmentType || merged.remoteType || '',
          level: merged.experienceLevel || merged.educationRequired || '',
        };
      case 'internships':
        return {
          salary: formatStipend(details, extraDetails) || (merged.isPaid === false ? 'Unpaid' : '—'),
          skills: normalizeSkills(merged.requiredSkills),
          type: merged.internshipType || merged.remoteType || '',
          level: merged.academicLevel || merged.educationLevel || '',
        };
      case 'scholarships':
        return {
          salary: merged.fundingType || merged.coverage || '—',
          skills: [],
          type: merged.scholarshipCategory || merged.fundingType || '',
          level: merged.degreeLevel || '',
        };
      case 'admissions':
        return {
          salary: merged.tuitionFee ? String(merged.tuitionFee) : '—',
          skills: [],
          type: merged.programName || '',
          level: merged.degreeLevel || '',
        };
      case 'events':
        return {
          salary: merged.ticketType || (merged.registrationFee ? `Fee: ${merged.registrationFee}` : '—'),
          skills: [],
          type: merged.eventType || merged.mode || '',
          level: merged.eventCategory || '',
        };
      default:
        return { salary: '—', skills: [], type: '', level: '' };
    }
  }

  function buildDetailFields(categorySlug, details, extraDetails) {
    const merged = { ...(extraDetails || {}), ...(details || {}) };
    const fields = [];
    const add = (label, value) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value) && !value.length) return;
      fields.push({
        label,
        value: Array.isArray(value) ? value.join(', ') : String(value),
      });
    };

    switch (categorySlug) {
      case 'jobs':
        add('Employment type', merged.employmentType);
        add('Workplace', merged.remoteType);
        add('Salary', formatSalaryRange(details, extraDetails));
        add('Experience', merged.experienceLevel);
        add('Education', merged.educationRequired);
        add('Skills', normalizeSkills(merged.requiredSkills));
        add('Openings', merged.openings);
        add('Application', merged.applicationType);
        add('Responsibilities', merged.responsibilities);
        break;
      case 'internships':
        add('Internship type', merged.internshipType);
        add('Workplace', merged.remoteType);
        add('Duration', merged.duration || (merged.durationMonths ? `${merged.durationMonths} months` : null));
        add('Stipend', formatStipend(details, extraDetails) || merged.stipendType);
        add('Education level', merged.academicLevel || merged.educationLevel);
        add('Skills', normalizeSkills(merged.requiredSkills));
        add('Certificate', merged.certificate);
        add('PPO opportunity', merged.ppo);
        add('Application', merged.applicationType);
        add('Responsibilities', merged.responsibilities);
        break;
      case 'scholarships':
        add('Category', merged.scholarshipCategory);
        add('Degree level', merged.degreeLevel);
        add('Country', merged.hostCountry || merged.country);
        add('Coverage', merged.coverage || merged.fundingType);
        add('Eligibility', merged.eligibility || merged.benefits);
        break;
      case 'admissions':
        add('Program', merged.programName);
        add('Institution', merged.institution);
        add('Intake', merged.intakeTerm || merged.intake);
        add('Tuition fee', merged.tuitionFee);
        add('Degree level', merged.degreeLevel);
        add('Tests required', merged.testRequirements);
        break;
      case 'events':
        add('Category', merged.eventCategory);
        add('Format', merged.eventType || merged.mode);
        add('Venue', merged.venue);
        add('Ticket', merged.ticketType);
        add('Price', merged.ticketPrice || merged.registrationFee);
        add('Registration', merged.registrationUrl);
        if (merged.startDatetime) add('Starts', new Date(merged.startDatetime).toLocaleString());
        if (merged.endDatetime) add('Ends', new Date(merged.endDatetime).toLocaleString());
        break;
      default:
        break;
    }

    return fields;
  }

  function mapOpportunity(row) {
    if (!row) return null;
    const category = row.categorySlug || row.category || 'jobs';
    const details = row.details || null;
    const extraDetails = row.extraDetails || null;
    const summary = buildSummaryFields(category, details, extraDetails);
    const salary = row.summarySalary || summary.salary || '—';
    const type = row.summaryType || summary.type || (row.isRemote ? 'Remote' : 'On-site');
    const level = row.summaryLevel || summary.level || '';
    const skills = summary.skills || [];
    const detailFields = buildDetailFields(category, details, extraDetails);

    return {
      id: row.publicId,
      dbId: row.id,
      category,
      title: row.title || 'Untitled',
      org: row.organization || 'Unknown',
      location: formatLocation(row),
      salary,
      daysLeft: daysLeftFromDeadline(row.deadline),
      posted: formatPosted(row.createdAt),
      source: row.sourceName || 'Opportunity Circle',
      type,
      level,
      skills,
      summary: row.description || '',
      applyUrl: row.applyUrl || '',
      deadline: row.deadline,
      createdAt: row.createdAt,
      countryCode: row.countryCode,
      isRemote: row.isRemote,
      details,
      extraDetails,
      detailFields,
      applicationType: extraDetails?.applicationType || 'External Apply',
      isInternalApply: (extraDetails?.applicationType || '') === 'Internal Apply',
      status: row.status || row.oppStatus || 'live',
      isExpired: (row.status || row.oppStatus || 'live') === 'expired',
    };
  }

  function buildQuery(params = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '' && val !== 'all') {
        q.set(key, String(val));
      }
    });
    const s = q.toString();
    return s ? `?${s}` : '';
  }

  const api = {
    baseUrl: API_BASE,

    health: () => apiFetch('/health'),

    getCategories: () => apiFetch('/api/categories'),

    getSources: () => apiFetch('/api/sources'),

    getOpportunities: async (params = {}) => {
      const apiParams = { limit: params.limit || 50, page: params.page || 1 };
      if (params.category && params.category !== 'all') apiParams.category = params.category;
      if (params.q) apiParams.q = params.q;
      if (params.sort) apiParams.sort = params.sort;
      if (params.is_remote !== undefined) apiParams.is_remote = params.is_remote;
      if (params.city) apiParams.city = params.city;
      if (params.country) apiParams.country = params.country;
      if (params.subcategory) apiParams.subcategory = params.subcategory;
      if (params.source) apiParams.source = params.source;
      if (params.deadline_before) apiParams.deadline_before = params.deadline_before;
      if (params.deadline_after) apiParams.deadline_after = params.deadline_after;
      const data = await apiFetch(`/api/opportunities${buildQuery(apiParams)}`);
      return {
        ...data,
        opportunities: (data.opportunities || []).map(mapOpportunity),
      };
    },

    getOpportunity: async (publicId) => {
      const data = await apiFetch(`/api/opportunities/${encodeURIComponent(publicId)}`);
      return mapOpportunity({
        ...data.opportunity,
        details: data.details,
        extraDetails: data.extraDetails,
      });
    },

    signup: (email, password, displayName, registerAsEmployer = false) =>
      apiFetch('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ email, password, displayName, registerAsEmployer }),
      }),

    login: (email, password) =>
      apiFetch('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    logout: () => apiFetch('/api/auth/logout', { method: 'POST' }),

    getMe: () => apiFetch('/api/users/me'),

    updateProfile: (patch) =>
      apiFetch('/api/users/me', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    registerAsEmployer: () =>
      apiFetch('/api/users/me', {
        method: 'PATCH',
        body: JSON.stringify({ registerAsEmployer: true }),
      }),

    getAdminEmployerProfiles: (params = {}) =>
      apiFetch(`/api/admin/employer-profiles${buildQuery({ status: params.status || 'pending' })}`),

    verifyAdminEmployerProfile: (userId) =>
      apiFetch(`/api/admin/employer-profiles/${userId}/verify`, { method: 'POST' }),

    rejectAdminEmployerProfile: (userId, reason) =>
      apiFetch(`/api/admin/employer-profiles/${userId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || '' }),
      }),

    getSaved: () => apiFetch('/api/saved/opportunities'),

    saveOpportunity: (opportunityId) =>
      apiFetch('/api/saved/opportunities', {
        method: 'POST',
        body: JSON.stringify({ opportunityId }),
      }),

    unsaveOpportunity: (opportunityId) =>
      apiFetch(`/api/saved/opportunities/${opportunityId}`, { method: 'DELETE' }),

    createStaged: (payload) =>
      apiFetch('/api/staged', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    getPendingStaged: (params = {}) =>
      apiFetch(`/api/staged${buildQuery({ limit: params.limit || 50 })}`),

    approveStaged: (id) =>
      apiFetch(`/api/staged/${id}/approve`, { method: 'POST' }),

    rejectStaged: (id, reason) =>
      apiFetch(`/api/staged/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || '' }),
      }),

    bulkApproveStaged: (ids) =>
      apiFetch('/api/staged/bulk-approve', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),

    getAdminPlatformStats: () => apiFetch('/api/admin/stats/platform'),

    getAdminReviewQueueStats: () => apiFetch('/api/admin/stats/review-queue'),

    getAdminIngestionStats: (params = {}) =>
      apiFetch(`/api/admin/stats/ingestion${buildQuery(params)}`),

    getAdminIngestionRuns: (params = {}) =>
      apiFetch(`/api/admin/ingestion-runs${buildQuery(params)}`),

    getAdminUsers: (params = {}) =>
      apiFetch(`/api/admin/users${buildQuery(params)}`),

    createAdminUser: (payload) =>
      apiFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateAdminUser: (id, patch) =>
      apiFetch(`/api/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    getAdminSources: () => apiFetch('/api/sources?includeInactive=true'),

    getSourceById: (id) => apiFetch(`/api/sources/${id}`),

    createSource: (payload) =>
      apiFetch('/api/sources', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    deleteSource: (id) =>
      apiFetch(`/api/sources/${id}`, { method: 'DELETE' }),

    updateSource: (id, patch) =>
      apiFetch(`/api/sources/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    runSource: (id) =>
      apiFetch(`/api/sources/${id}/run`, { method: 'POST' }),

    getWhatsAppIntegration: () => apiFetch('/api/admin/whatsapp-integration/settings'),

    updateWhatsAppIntegration: (patch) =>
      apiFetch('/api/admin/whatsapp-integration/settings', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    testWhatsAppIntegration: () =>
      apiFetch('/api/admin/whatsapp-integration/test-connection', { method: 'POST' }),

    runWhatsAppIngest: () =>
      apiFetch('/api/admin/whatsapp-integration/ingest', { method: 'POST' }),

    getCommunityChannels: () => apiFetch('/api/community/channels'),
    getCommunityStats: () => apiFetch('/api/community/stats'),
    getCommunityTestimonials: () => apiFetch('/api/community/testimonials'),
    getCommunityChapters: () => apiFetch('/api/community/chapters'),
    getCommunityTopReviewers: (params = {}) =>
      apiFetch(`/api/community/top-reviewers${buildQuery(params)}`),
    submitCommunityShare: (payload) =>
      apiFetch('/api/community/share', { method: 'POST', body: JSON.stringify(payload) }),
    submitReviewerApplication: (payload) =>
      apiFetch('/api/community/reviewer-applications', { method: 'POST', body: JSON.stringify(payload) }),
    subscribeNewsletter: (email, source = 'website') =>
      apiFetch('/api/community/newsletter', { method: 'POST', body: JSON.stringify({ email, source }) }),

    getResourceTopics: () => apiFetch('/api/resources/topics'),
    getResourceGuides: (params = {}) =>
      apiFetch(`/api/resources/guides${buildQuery(params)}`),
    getResourceGuide: (slug) => apiFetch(`/api/resources/guides/${encodeURIComponent(slug)}`),

    getSupportPage: (slug) => apiFetch(`/api/support/pages/${encodeURIComponent(slug)}`),
    submitContact: (payload) =>
      apiFetch('/api/support/contact', { method: 'POST', body: JSON.stringify(payload) }),

    getAdminNewsletterSubscribers: () => apiFetch('/api/admin/newsletter-subscribers'),

    getAdminCommunityChannels: () => apiFetch('/api/admin/community/channels'),
    createAdminCommunityChannel: (payload) =>
      apiFetch('/api/admin/community/channels', { method: 'POST', body: JSON.stringify(payload) }),
    patchAdminCommunityChannel: (id, patch) =>
      apiFetch(`/api/admin/community/channels/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

    getAdminTestimonials: () => apiFetch('/api/admin/community/testimonials'),
    createAdminTestimonial: (payload) =>
      apiFetch('/api/admin/community/testimonials', { method: 'POST', body: JSON.stringify(payload) }),
    patchAdminTestimonial: (id, patch) =>
      apiFetch(`/api/admin/community/testimonials/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

    getAdminUniversityChapters: () => apiFetch('/api/admin/university-chapters'),
    createAdminUniversityChapter: (payload) =>
      apiFetch('/api/admin/university-chapters', { method: 'POST', body: JSON.stringify(payload) }),
    patchAdminUniversityChapter: (id, patch) =>
      apiFetch(`/api/admin/university-chapters/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    getAdminReviewerApplications: (params = {}) =>
      apiFetch(`/api/admin/reviewer-applications${buildQuery(params)}`),
    updateAdminReviewerApplication: (id, patch) =>
      apiFetch(`/api/admin/reviewer-applications/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    getAdminResourceGuides: () => apiFetch('/api/admin/resources/guides'),
    createAdminResourceGuide: (payload) =>
      apiFetch('/api/admin/resources/guides', { method: 'POST', body: JSON.stringify(payload) }),
    patchAdminResourceGuide: (id, patch) =>
      apiFetch(`/api/admin/resources/guides/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

    getApplications: () => apiFetch('/api/applications'),

    createApplication: (payload) =>
      apiFetch('/api/applications', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    uploadResume: async (file) => {
      const form = new FormData();
      form.append('resume', file);
      let res;
      try {
        res = await fetch(`${API_BASE}/api/applications/upload-resume`, {
          method: 'POST',
          credentials: 'include',
          body: form,
        });
      } catch (err) {
        throw new Error(
          `Cannot reach API at ${API_BASE}. Start the backend: cd opportunity_circle_be && npm run dev`
        );
      }
      let data = {};
      try {
        data = await res.json();
      } catch (e) {
        data = {};
      }
      if (!res.ok) {
        const err = new Error(data.message || `Upload failed (${res.status})`);
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    },

    downloadApplicationResume: async (applicationId, filename = 'resume.pdf') => {
      let res;
      try {
        res = await fetch(`${API_BASE}/api/applications/${applicationId}/resume`, {
          credentials: 'include',
        });
      } catch (err) {
        throw new Error('Could not download resume');
      }
      if (!res.ok) {
        let data = {};
        try {
          data = await res.json();
        } catch (e) {
          data = {};
        }
        throw new Error(data.message || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const downloadName = match?.[1] || filename;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },

    updateApplication: (id, patch) =>
      apiFetch(`/api/applications/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    deleteApplication: (id) =>
      apiFetch(`/api/applications/${id}`, { method: 'DELETE' }),

    getSavedSearches: () => apiFetch('/api/saved/searches'),

    createSavedSearch: (payload) =>
      apiFetch('/api/saved/searches', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateSavedSearch: (id, patch) =>
      apiFetch(`/api/saved/searches/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    deleteSavedSearch: (id) =>
      apiFetch(`/api/saved/searches/${id}`, { method: 'DELETE' }),

    previewSavedSearch: (id, params = {}) =>
      apiFetch(`/api/saved/searches/${id}/preview${buildQuery({ limit: params.limit || 12 })}`),

    getAlertHistory: (params = {}) =>
      apiFetch(`/api/saved/alerts${buildQuery({
        page: params.page || 1,
        limit: params.limit || 20,
      })}`),

    markAlertRead: (id) =>
      apiFetch(`/api/saved/alerts/${id}/read`, { method: 'PATCH' }),

    unsubscribeAlertByToken: (token) =>
      apiFetch(`/api/saved/alerts/unsubscribe${buildQuery({ token })}`),

    getEmployerProfile: () => apiFetch('/api/employer/profile'),

    saveEmployerProfile: (payload) =>
      apiFetch('/api/employer/profile', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    getEmployerListings: () => apiFetch('/api/employer/listings'),

    getEmployerApplicants: () => apiFetch('/api/employer/applicants'),

    updateEmployerApplicant: (id, patch) =>
      apiFetch(`/api/employer/applicants/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    getEmployerStats: () => apiFetch('/api/employer/stats'),

    closeEmployerListing: (publicId) =>
      apiFetch(`/api/employer/listings/${encodeURIComponent(publicId)}/close`, { method: 'PATCH' }),

    mapEmployerListing(row) {
      if (!row) return null;
      return {
        id: row.id,
        stagedId: row.stagedId,
        publicId: row.publicId,
        opportunityId: row.opportunityId,
        category: row.category || 'jobs',
        title: row.title || 'Untitled',
        org: row.org || 'Unknown',
        location: row.location || 'Location TBD',
        status: row.status || 'pending',
        postedAt: row.postedAt || formatPosted(row.createdAt),
        createdAt: row.createdAt,
        applicants: row.applicants ?? row.applicationCount ?? 0,
        applicationCount: row.applicationCount ?? row.applicants ?? 0,
      };
    },

    mapEmployerApplicant(row) {
      if (!row) return null;
      return {
        id: row.id,
        name: row.name || row.email || 'Applicant',
        email: row.email || '',
        appliedFor: row.appliedFor || 'Untitled',
        date: row.date || formatPosted(row.appliedAt),
        status: row.status || 'applied',
        opportunityPublicId: row.opportunityPublicId,
        appliedAt: row.appliedAt,
        coverLetter: row.coverLetter || '',
        applyChannel: row.applyChannel || 'external',
        resumeUrl: row.resumeUrl || null,
      };
    },

    mapEmployerProfile(row) {
      if (!row) return null;
      return {
        companyName: row.companyName || '',
        website: row.website || '',
        industry: row.industry || '',
        companySize: row.companySize || '',
        headquarters: row.headquarters || '',
        email: row.email || '',
        phone: row.phone || '',
        about: row.about || '',
        verificationStatus: row.verificationStatus || 'draft',
        submitted: !!row.submitted,
        starRating: row.starRating || 0,
        reviewCount: row.reviewCount || 0,
        rejectionReason: row.rejectionReason || '',
      };
    },

    buildDetailPayload(categorySlug, fields) {
      if (!fields) return undefined;
      if (categorySlug === 'jobs') {
        return {
          employmentType: fields.employmentType,
          remoteType: fields.workplaceType,
          salaryMin: fields.salaryMin ? Number(fields.salaryMin) : undefined,
          salaryMax: fields.salaryMax ? Number(fields.salaryMax) : undefined,
          currency: fields.currency,
          experienceLevel: fields.experienceRequired,
          requiredSkills: fields.requiredSkills,
          educationRequired: fields.educationRequired,
          responsibilities: fields.responsibilities,
          openings: fields.openings ? Number(fields.openings) : undefined,
          applicationType: fields.applicationType,
          jobCategory: fields.jobCategory,
          salaryType: fields.salaryType,
        };
      }
      if (categorySlug === 'internships') {
        const durationMatch = String(fields.duration || '').match(/(\d+)/);
        return {
          internshipType: fields.internshipType,
          remoteType: fields.workplaceType,
          durationMonths: durationMatch ? Number(durationMatch[1]) : undefined,
          duration: fields.duration,
          isPaid: fields.stipendType === 'Paid',
          stipendAmount: fields.stipendAmount ? Number(fields.stipendAmount) : undefined,
          stipendType: fields.stipendType,
          academicLevel: fields.educationLevel,
          requiredSkills: fields.requiredSkills,
          responsibilities: fields.responsibilities,
          certificate: fields.certificate,
          ppo: fields.ppo,
          openings: fields.openings ? Number(fields.openings) : undefined,
          applicationType: fields.applicationType,
          internshipCategory: fields.internshipCategory,
        };
      }
      if (categorySlug === 'scholarships') {
        return {
          scholarshipCategory: fields.scholarshipCategory,
          degreeLevel: fields.degreeLevel,
          hostCountry: fields.country,
          country: fields.country,
          eligibility: fields.eligibility,
          coverage: fields.coverage,
          fundingType: fields.coverage,
          benefits: fields.eligibility,
        };
      }
      if (categorySlug === 'admissions') {
        return {
          programName: fields.programName,
          institution: fields.institution,
          intakeTerm: fields.intake,
          intake: fields.intake,
          tuitionFee: fields.tuitionFee,
        };
      }
      if (categorySlug === 'events') {
        return {
          eventCategory: fields.eventCategory,
          eventType: fields.eventType,
          mode: fields.eventType,
          venue: fields.venue,
          ticketType: fields.ticketType,
          ticketPrice: fields.ticketPrice ? Number(fields.ticketPrice) : undefined,
          registrationFee: fields.ticketPrice ? Number(fields.ticketPrice) : undefined,
          registrationUrl: fields.registrationUrl,
          startTime: fields.startTime,
          endTime: fields.endTime,
        };
      }
      return { ...fields };
    },

    mapApplication(row) {
      if (!row) return null;
      return {
        id: row.publicId,
        applicationId: row.id,
        opportunityId: row.opportunityId,
        status: row.status || 'applied',
        notes: row.notes || '',
        coverLetter: row.coverLetter || '',
        applyChannel: row.applyChannel || 'external',
        resumeUrl: row.resumeUrl || null,
        date: formatPosted(row.appliedAt),
        appliedAt: row.appliedAt,
        title: row.title || 'Untitled',
        org: row.organization || 'Unknown',
        category: row.categorySlug || 'jobs',
        location: formatLocation(row),
        applyUrl: row.applyUrl || '',
        deadline: row.deadline,
      };
    },

    mapOpportunity,
    buildDetailFields,
    buildSummaryFields,

    buildSourcePayload(form) {
      if (!form) return null;
      const payload = {
        name: form.name,
        slug: form.slug,
        type: form.type,
        requiresReview: form.requiresReview !== false,
        isActive: form.isActive !== false,
      };
      if (form.type === 'rss') {
        payload.config = { feedUrl: form.feedUrl };
      } else if (form.type === 'ats') {
        payload.config = { boardToken: form.boardToken };
      } else if (form.type === 'free_api' || form.type === 'paid_api') {
        payload.config = {
          provider: form.provider || 'adzuna',
          country: form.country || 'gb',
          what: form.what || '',
          where: form.where || '',
        };
      }
      return payload;
    },
  };
export default api;
